import {
  Button,
  Col,
  Divider,
  Input,
  InputNumber,
  Row,
  Space,
  Switch,
  notification,
  Spin,
} from 'antd'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { fetchModel3DFile } from '$api/client'
import { ExpandableTableCard, Title } from './styledComponents'
import Form from '$components/Form'
import BumpmapUpload from './BumpmapUpload'
import PropTypes from 'prop-types'
import { useQuery } from '@tanstack/react-query'

const { TextArea } = Input

const TexturingConfigForm = (props) => {
  const saved = props.rowDataRef.current[props.record.id]
  const initialImageFile = {
    file: null,
    isPreset: false,
    presetName: null,
    textureId: null,
  }
  const [imageFile, setImageFile] = useState(
    saved?.imageFile || initialImageFile,
  )

  const [form] = Form.useForm()
  const visibleSide = Form.useWatch('visibleSide', form)
  const notVisibleSide = Form.useWatch('notVisibleSide', form)
  const { t } = useTranslation()

  const { data: model3DFile, isLoading } = useQuery({
    queryKey: ['model3DFile', props.record.id],
    queryFn: ({ signal }) => fetchModel3DFile(props.record.id, signal),
    staleTime: Infinity,
  })

  useEffect(() => {
    if (props.rowDataRef.current[props.record.id]) {
      form.setFieldsValue(props.rowDataRef.current[props.record.id])
    }
  }, [])

  const onFinish = (values) => {
    if (!imageFile.file) {
      openErrorNotification(
        'top',
        t('service.apply_texturing.must_select_or_upload_bumpmap'),
      )
      return
    }

    // set status for each part to "finished"
    props.setStatus([true, props.record.id])
    props.rowDataRef.current[props.record.id] = {
      ...props.rowDataRef.current[props.record.id],
      status: true,
    }
    // post to endpoints
    const schema = {
      service_id: 'string',
      company: 'string',
      service_name: 'string',
      part: {
        id: props.record.id,
        name: props.record.name,
        id_part_client: props.record.id_part_client,
        db_id_client: props.record.id,
        company: props.record.company,
        attributes: props.record.attributes,
        float_attributes: props.record.float_attributes,
        status_attributes: props.record.status_attributes,
        dwh_materials: props.record.dwh_materials,
        client_material_name: props.record.client_material.name,
      },
      visible_side: values.visibleSide,
      visible_where: values.visibleWhere,
      not_visible_side: values.notVisibleSide ?? false,
      specifications: values.specifications,
      depth_of_grain: values.depthOfGrain,
      user_texture_file: imageFile,
      model_3d_file: model3DFile,
    }
    props.setCurrentFormValues(schema)
  }

  const onReset = () => {
    openNotification('top', 'Änderungen verworfen')
    form.resetFields()
  }

  const openNotification = (placement, message) => {
    notification.success({
      message: `${message}`,
      description: ``,
      placement,
    })
  }

  const openErrorNotification = (placement, message) => {
    notification.error({
      message: `${message}`,
      description: ``,
      placement,
    })
  }

  const resetRecordStatus = (recordId) => {
    props.rowDataRef.current[props.record.id] = {
      ...props.rowDataRef.current[props.record.id],
      status: false,
    }
    props.setStatus((prev) => {
      if (prev[0] === false && prev[1] === recordId) return prev
      return [false, recordId]
    })
  }

  const onImageFileChange = (file, recordId) => {
    setImageFile(file)
    props.rowDataRef.current[recordId] = {
      ...props.rowDataRef.current[recordId],
      imageFile: file,
    }
  }

  const onImageFileRemove = (recordId) => {
    resetRecordStatus(recordId)
    setImageFile(initialImageFile)
    props.rowDataRef.current[recordId] = {
      ...props.rowDataRef.current[recordId],
      imageFile: initialImageFile,
    }
  }

  const handleSwitchChange = (name, checked) => {
    form.setFieldValue(name, checked)
    props.rowDataRef.current[props.record.id] = {
      ...props.rowDataRef.current[props.record.id],
      [name]: checked,
    }
    resetRecordStatus(props.record.id)
  }

  return (
    <Spin
      spinning={isLoading}
      tip={t('service.apply_texturing.model_3d_processing')}
    >
      <ExpandableTableCard>
        <Form
          layout="vertical"
          size="medium"
          onFinish={onFinish}
          onReset={onReset}
          form={form}
          onValuesChange={(_, allValues) => {
            resetRecordStatus(props.record.id)
            props.rowDataRef.current[props.record.id] = {
              ...props.rowDataRef.current[props.record.id],
              ...allValues,
            }
          }}
          initialValues={{
            depthOfGrain: 100,
            visibleSide: false,
            notVisibleSide: false,
          }}
        >
          <Col span={24}>
            <Row className="row">
              <Col flex="1 0 25%" className="formcolumn">
                <Title style={{ margin: '23px 0px' }}>
                  {t('service.apply_texturing.please_upload_or_choose')}
                </Title>
                <BumpmapUpload
                  imageFile={imageFile}
                  setImageFile={(file) =>
                    onImageFileChange(file, props.record.id)
                  }
                  onRemove={() => onImageFileRemove(props.record.id)}
                />
              </Col>
              <Divider
                type="vertical"
                className="partsdivider"
                style={{ marginTop: '67px' }}
              />
              <Col flex="1 0 25%" className="formcolumn">
                <Title style={{ margin: '23px 0px' }}>
                  {t('Additional information')} ({t('Optional')})
                </Title>
                <Form.Item
                  label={t('service.apply_texturing.depth_of_surface_texture')}
                  name="depthOfGrain"
                  rules={[
                    {
                      required: true,
                      message: t(
                        'service.apply_texturing.missing_depth_of_grain',
                      ),
                    },
                  ]}
                >
                  <InputNumber
                    className="white-bg"
                    style={{ width: '100%' }}
                    variant="borderless"
                    min={0}
                    max={1000}
                    formatter={(value) => `${value} µm`}
                    parser={(value) => value.replace(' µm', '')}
                    // onChange={onChange}
                  />
                  {/* <TextArea rows={4} variant='borderless' className="white-bg" defaultValue="" /> */}
                </Form.Item>
                <Form.Item
                  label={t('service.apply_texturing.additional_specifications')}
                  name="specifications"
                >
                  <TextArea
                    rows={4}
                    variant="borderless"
                    className="white-bg"
                  />
                </Form.Item>
              </Col>
              <Divider
                type="vertical"
                className="partsdivider"
                style={{ marginTop: '67px' }}
              />
              <Col flex="1 0 25%" className="formcolumn formcolumnlast">
                <Title style={{ margin: '23px 0px' }}>
                  {t('service.apply_texturing.visible_side')}
                </Title>
                <Form.Item
                  label={t('Is there a visible side')}
                  name="visibleSide"
                  valuePropName="checked"
                >
                  <div
                    style={{ display: 'flex', flexDirection: 'row-reverse' }}
                  >
                    <Switch
                      checked={visibleSide}
                      onChange={(checked) =>
                        handleSwitchChange('visibleSide', checked)
                      }
                    />
                  </div>
                </Form.Item>
                {visibleSide ? (
                  <>
                    <Form.Item
                      label={t('Where?')}
                      name="visibleWhere"
                      style={{ marginTop: -30 }}
                    >
                      <TextArea
                        rows={4}
                        variant="borderless"
                        className="white-bg"
                      />
                    </Form.Item>
                    <Form.Item
                      className="longlabelcheckbox"
                      label={
                        <div
                          style={{
                            overflowWrap: 'break-word',
                            wordBreak: 'break-word',
                            display: 'inline-block',
                            whiteSpace: 'break-spaces',
                          }}
                        >
                          {t('service.apply_texturing.only_on_visible_side')}
                        </div>
                      }
                      name="notVisibleSide"
                      valuePropName="checked"
                    >
                      <div
                        style={{
                          display: 'flex',
                          flexDirection: 'row-reverse',
                        }}
                      >
                        <Switch
                          checked={notVisibleSide}
                          onChange={(checked) =>
                            handleSwitchChange('notVisibleSide', checked)
                          }
                        />
                      </div>
                    </Form.Item>
                  </>
                ) : null}
              </Col>
            </Row>
          </Col>
          <Form.Item style={{ float: 'right', marginTop: '20px' }}>
            <Space>
              <Button
                type="primary"
                htmlType="submit"
                disabled={props.getRecordStatus(props.record.id)}
              >
                {t('button.save')}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </ExpandableTableCard>
    </Spin>
  )
}

TexturingConfigForm.propTypes = {
  record: PropTypes.object.isRequired,
  setStatus: PropTypes.func.isRequired,
  getRecordStatus: PropTypes.func.isRequired,
  rowDataRef: PropTypes.object.isRequired,
}

export default TexturingConfigForm
